import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// The last signed-in page, so a sign-in (password or Microsoft, both of which
// land on "/") returns to it rather than to the default landing page. Per
// browser; a page the account may not open is redirected by ProtectedRoute as
// usual. Path and query string, so a page that keeps its state in the URL (the
// Mailbox's folder and message) comes back as it was.
export const LAST_PAGE_KEY = 'delair.lastPage';
const NOT_REMEMBERED = ['/', '/login', '/reset-password', '/no-access'];

export const lastPage = () => {
  try {
    const p = localStorage.getItem(LAST_PAGE_KEY);
    // "//..." is a protocol-relative URL, not a path: the router crashes on it.
    return p && p.startsWith('/') && !p.startsWith('//') && !NOT_REMEMBERED.includes(p.split('?')[0])
      ? p
      : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const keepAliveIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const SESSION_REFRESH_INTERVAL = 25 * 60 * 1000;
  const ACTIVITY_TIMEOUT = 30 * 60 * 1000;

  const updateActivity = () => {
    lastActivityRef.current = Date.now();
  };

  useEffect(() => {
    axios.defaults.withCredentials = true;
  }, []);

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      updateActivity();
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, []);

  const startKeepAlive = () => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
    }

    keepAliveIntervalRef.current = setInterval(async () => {
      try {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        if (timeSinceLastActivity > ACTIVITY_TIMEOUT) {
          console.log('User inactive for too long, logging out');
          logout();
          navigate('/login', { replace: true });
          return;
        }

        if (isAuthenticated) {
          const refreshResponse = await axios.post('/api/auth/refresh');

          if (refreshResponse.status === 200) {
            const userResponse = await axios.get('/api/auth/me');
            if (userResponse.data) {
              setUser(userResponse.data);
            }
            console.log('Session refreshed with new token');
          }
        }
      } catch (error) {
        console.error('Session refresh failed:', error);
        if (error.response?.status === 401) {
          logout();
          navigate('/login', { replace: true });
        }
      }
    }, SESSION_REFRESH_INTERVAL);
  };

  const stopKeepAlive = () => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  };

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        updateActivity();
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        updateActivity();
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest?._retry) {
          const url = originalRequest?.url || '';
          // Login failure or session probe: do not force navigation
          if (url.includes('auth/token') || (!isAuthenticated && url.includes('auth/me'))) {
            return Promise.reject(error);
          }
          originalRequest._retry = true;
          setIsAuthenticated(false);
          setUser(null);
          stopKeepAlive();
          navigate('/login', { replace: true });
          return Promise.reject(error);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    verifyToken();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || NOT_REMEMBERED.includes(location.pathname)) return;
    try {
      localStorage.setItem(LAST_PAGE_KEY, `${location.pathname}${location.search}`);
    } catch {
      /* no storage (private window): sign-in lands on the default page */
    }
  }, [isAuthenticated, location.pathname, location.search]);

  useEffect(() => {
    if (isAuthenticated) {
      startKeepAlive();
    } else {
      stopKeepAlive();
    }

    return () => {
      stopKeepAlive();
    };
  }, [isAuthenticated]);

  const verifyToken = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      await axios.post('/api/auth/token', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      await verifyToken();
      return true;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (e) {
      /* still clear local state if the API fails */
    }
    setIsAuthenticated(false);
    setUser(null);
    stopKeepAlive();
  };

  const isAdmin = () => {
    return user?.is_superuser === true;
  };

  // /auth/me resolves an admin to the full catalog, so this needs no is_superuser
  // special case. It gates what gets rendered only -- the API enforces the same
  // rule independently, since anything here is reachable from a devtools console.
  const hasFeature = (feature) => {
    return Array.isArray(user?.features) && user.features.includes(feature);
  };

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    loading,
    isAdmin,
    hasFeature,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
